import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
// Foundation SaaS marketing site (the always-Foundation shell)
import AgencyAbout from "@/pages/AgencyAbout";
import AgencyContact from "@/pages/AgencyContact";
import AgencySell from "@/pages/AgencySell";
import AgencyLet from "@/pages/AgencyLet";
import AgencyGuides from "@/pages/AgencyGuides";
import AgencyGuideDetail from "@/pages/AgencyGuideDetail";
import AgencyLegal from "@/pages/AgencyLegal";
import AgencyFees from "@/pages/AgencyFees";

// Tenant-branded surface (uses config/brand.json / /api/brand)
import Landing from "@/pages/Landing";
import AgencySite from "@/pages/AgencySite";
import Valuation from "@/pages/Valuation";
import PublicListings from "@/pages/PublicListings";
import AreaLandingPage from "@/pages/AreaLandingPage";
import AreaPricesPage from "@/pages/AreaPricesPage";
import PublicListingDetail from "@/pages/PublicListingDetail";
import { BrandProvider, TenantScope, useBrand, useMarket } from "@/lib/brand";
import NotFound from "@/pages/NotFound";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ScrollToTop from "@/components/ScrollToTop";

// ---------------------------------------------------------------------------
// Route-level code splitting.
//
// Every page in the product used to arrive in one bundle — 463KB gzipped,
// 1.8MB raw — so a homeowner opening a valuation form on an agency's website
// downloaded the entire CRM, the admin console, the client portal and
// Foundation's own marketing site before anything could render.
//
// The public tenant surface (home, valuation, listings, area and the agency
// content pages) stays eagerly imported: it is the fast path, it is what
// Google lands on, and splitting it would only trade bytes for a round trip.
// Everything behind a login, and everything belonging to Foundation rather
// than to the agency, is loaded on demand.
// ---------------------------------------------------------------------------
const FoundationLegal = lazy(() => import("@/pages/FoundationLegal"));
const FoundationAdminLogin = lazy(() => import("@/pages/FoundationAdmin").then((m) => ({ default: m.FoundationAdminLogin })));
const FoundationAdminDashboard = lazy(() => import("@/pages/FoundationAdmin").then((m) => ({ default: m.FoundationAdminDashboard })));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const SignPage = lazy(() => import("@/pages/SignPage"));
const PortalLogin = lazy(() => import("@/pages/portal/PortalLogin"));
const PortalConsume = lazy(() => import("@/pages/portal/PortalConsume"));
const PortalDashboard = lazy(() => import("@/pages/portal/PortalDashboard"));
// The buyer's own portal, and online showing requests. Public pages.
const BuyerPortal = lazy(() => import("@/pages/portal/BuyerPortal"));
const BookShowing = lazy(() => import("@/pages/BookShowing"));
const ManageShowing = lazy(() => import("@/pages/ManageShowing"));
const CrmOnlineShowings = lazy(() => import("@/pages/crm/CrmOnlineShowings"));
const CmaRequest = lazy(() => import("@/pages/CmaRequest"));
const MarketReport = lazy(() => import("@/pages/MarketReport"));
const RegisterApplicant = lazy(() => import("@/pages/RegisterApplicant"));
const CalculatorsIndex = lazy(() => import("@/pages/calculators/CalculatorsIndex"));
const MortgageCalculator = lazy(() => import("@/pages/calculators/MortgageCalculator"));
const AffordabilityCalculator = lazy(() => import("@/pages/calculators/AffordabilityCalculator"));
const RentalReturnCalculator = lazy(() => import("@/pages/calculators/RentalReturnCalculator"));
const ClosingCostsCalculator = lazy(() => import("@/pages/calculators/ClosingCostsCalculator"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const CrmDemoEntry = lazy(() => import("@/pages/CrmDemoEntry"));
// Where a paid ad lands: a free trial when STATEHOUSE_TRIAL_OPEN is on, the
// waitlist when it is off. The server decides; see backend/trial.py.
const StartTrial = lazy(() => import("@/pages/StartTrial"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminBrand = lazy(() => import("@/pages/AdminBrand"));
const AdminHelp = lazy(() => import("@/pages/AdminHelp"));
const CrmLayout = lazy(() => import("@/pages/crm/CrmLayout"));
const CrmOverview = lazy(() => import("@/pages/crm/CrmOverview"));
const CrmContacts = lazy(() => import("@/pages/crm/CrmContacts"));
const CrmReviews = lazy(() => import("@/pages/crm/CrmReviews"));
const CrmWebChat = lazy(() => import("@/pages/crm/CrmWebChat"));
const CrmOfficeInbox = lazy(() => import("@/pages/crm/CrmOfficeInbox"));
const CrmWriting = lazy(() => import("@/pages/crm/CrmWriting"));
const CrmContactDetail = lazy(() => import("@/pages/crm/CrmContactDetail"));
const CrmProperties = lazy(() => import("@/pages/crm/CrmProperties"));
const CrmPropertyDetail = lazy(() => import("@/pages/crm/CrmPropertyDetail"));
const AgreementSignPage = lazy(() => import("@/pages/AgreementSignPage"));
const CrmAgencyTerms = lazy(() => import("@/pages/crm/CrmAgencyTerms"));
// The pattern workshop. Not a product screen: it renders the shared
// components in every state so they can be checked against the design
// without hunting for a property that happens to have a failed feed.
const DesignStates = lazy(() => import("@/pages/crm/DesignStates"));
// Screens made of screens: nine destinations moved down a level in the
// redesign and now share a parent. See lib/crmNav.js for the whole map.
const CrmSettings = lazy(() => import("@/pages/crm/CrmSettings"));
const CrmWebsite = lazy(() => import("@/pages/crm/CrmWebsite"));
const CrmTasksScreen = lazy(() => import("@/pages/crm/CrmTasksScreen"));
const CrmTeamBoardScreen = lazy(() => import("@/pages/crm/CrmTeamBoardScreen"));
const CrmLettingsScreen = lazy(() => import("@/pages/crm/CrmLettingsScreen"));
const MovedRoute = lazy(() => import("@/pages/crm/MovedRoute"));
const CrmDocumentBrand = lazy(() => import("@/pages/crm/CrmDocumentBrand"));
const CrmOffers = lazy(() => import("@/pages/crm/CrmOffers"));
const CrmTransactions = lazy(() => import("@/pages/crm/CrmTransactions"));
const CrmReferrals = lazy(() => import("@/pages/crm/CrmReferrals"));
const CrmCommissionExport = lazy(() => import("@/pages/crm/CrmCommissionExport"));
const CrmBuyers = lazy(() => import("@/pages/crm/CrmBuyers"));
const CrmBuyerDetail = lazy(() => import("@/pages/crm/CrmBuyerDetail"));
const CrmBuyerTour = lazy(() => import("@/pages/crm/CrmBuyerTour"));
const CrmBuyerFile = lazy(() => import("@/pages/crm/CrmBuyerFile"));
const CrmGoals = lazy(() => import("@/pages/crm/CrmGoals"));
const CrmSmartLists = lazy(() => import("@/pages/crm/CrmSmartLists"));
const CrmImport = lazy(() => import("@/pages/crm/CrmImport"));
const CrmSpeedToLead = lazy(() => import("@/pages/crm/CrmSpeedToLead"));
const CrmOpenHouses = lazy(() => import("@/pages/crm/CrmOpenHouses"));
const CrmSellerReport = lazy(() => import("@/pages/crm/CrmSellerReport"));
const CrmStateRules = lazy(() => import("@/pages/crm/CrmStateRules"));
const CrmAsk = lazy(() => import("@/pages/crm/CrmAsk"));
const CrmViewings = lazy(() => import("@/pages/crm/CrmViewings"));
const CrmTasks = lazy(() => import("@/pages/crm/CrmTasks"));
const CrmReports = lazy(() => import("@/pages/crm/CrmReports"));
const CrmSocial = lazy(() => import("@/pages/crm/CrmSocial"));
const CrmEmails = lazy(() => import("@/pages/crm/CrmEmails"));
const CrmLettings = lazy(() => import("@/pages/crm/CrmLettings"));
const CrmTeam = lazy(() => import("@/pages/crm/CrmTeam"));
const CrmTemplates = lazy(() => import("@/pages/crm/CrmTemplates"));
const CrmGuides = lazy(() => import("@/pages/crm/CrmGuides"));
const CrmSequences = lazy(() => import("@/pages/crm/CrmSequences"));
const CrmBranches = lazy(() => import("@/pages/crm/CrmBranches"));
const CrmReach = lazy(() => import("@/pages/crm/CrmReach"));
const CrmSignal = lazy(() => import("@/pages/crm/CrmSignal"));
const CrmAccounts = lazy(() => import("@/pages/crm/CrmAccounts"));
const CrmInvoices = lazy(() => import("@/pages/crm/CrmInvoices"));
const CrmAppraisals = lazy(() => import("@/pages/crm/CrmAppraisals"));
const CrmInventory = lazy(() => import("@/pages/crm/CrmInventory"));
const CrmInventoryDetail = lazy(() => import("@/pages/crm/CrmInventoryDetail"));
const CrmCalendar = lazy(() => import("@/pages/crm/CrmCalendar"));
const CrmExports = lazy(() => import("@/pages/crm/CrmExports"));
const CrmPlans = lazy(() => import("@/pages/crm/CrmPlans"));
const FoundationTerms = lazy(() => import("@/pages/FoundationTerms"));
const FoundationSubProcessors = lazy(() => import("@/pages/FoundationSubProcessors"));
const AccountExport = lazy(() => import("@/pages/AccountExport"));


export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      {/* One boundary around the whole table. The fallback is deliberately
          blank rather than a spinner: the eagerly-bundled public pages never
          suspend, so this only ever shows while a lazily-loaded chunk is in
          flight, and a flashed spinner on a fast connection reads as jank. */}
      <Suspense fallback={<div style={{ minHeight: "60vh" }} />}>
      <Routes>
        {/* Public surfaces: the tenant's agency site, billing, legal. */}
        <Route element={<AppShell />}>
          {/* Statehouse's marketing site is its own repo and its own host
              (getstatehouse.com); this app serves the product, the tenant's
              own agency site, and the legal documents a signed-in brokerage
              is pointed at. The Foundation marketing pages that used to live
              here — home, pricing, blog, /platform and the ten /product/*
              pages — were UK copy in sterling, and they were reachable on
              this server: /pricing answered 200 with "UK-ONLY FOR NOW ·
              PRICES IN £ · HM LAND REGISTRY". Deleted rather than hidden. */}
          <Route path="/" element={<BrandedPage><AgencySite /></BrandedPage>} />
          {/* /checkout/:lookup_key is gone with the marketing pages. It was
              Foundation's sign-up form: sterling prices against Foundation's
              own Stripe catalogue under Software Builders Ltd, so it could
              not have taken a US payment correctly. The two /payment routes
              below stay — the backend's Stripe session redirects to them, so
              they are the return path of a real flow. Statehouse needs its
              own checkout before it can charge anybody. */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/about" element={<BrandedPage><AgencyAbout /></BrandedPage>} />
          <Route path="/legal/privacy" element={<FoundationLegal kind="privacy" />} />
          {/* The real Platform Terms, served from backend/legal/terms/. NOT
              at /terms — that path is the AGENCY's own terms page on their
              white-label site, and putting Foundation's contract there would
              replace every customer's. `/legal/terms/v/:version` is the
              frozen address a signed acceptance can point at forever. */}
          <Route path="/legal/terms" element={<FoundationTerms />} />
          <Route path="/legal/terms/v/:version" element={<FoundationTerms />} />
          <Route path="/legal/sub-processors" element={<FoundationSubProcessors />} />
          <Route path="/legal/cookies" element={<FoundationLegal kind="cookies" />} />

          {/* Public e-signature page — tokenised URL, no auth. */}
          <Route path="/sign/:token" element={<SignPage />} />
          {/* Agency agreements sign on their own path: the token is a
              different shape from the document one above, so sharing a
              route would mean guessing which format arrived. */}
          <Route path="/agreement/:token" element={<AgreementSignPage />} />

          {/* Foundation team super-admin (business dashboard) */}
          {/* Clause 9.2's export window. Outside the CRM shell on purpose:
              the account it exists for is the one the CRM answers 402 to. */}
          <Route path="/admin/export" element={<AccountExport />} />
          <Route path="/foundation-admin/login" element={<FoundationAdminLogin />} />
          <Route path="/foundation-admin" element={<FoundationAdminDashboard />} />

          {/* Legacy tenant-branded routes (resolve to DEFAULT_TENANT_SLUG — Foundation's own demo tenant, never a customer's) */}
          <Route path="/try" element={<Landing />} />
          <Route path="/valuation" element={<ValuationRoute />} />
          <Route path="/market-report" element={<MarketReport />} />
          <Route path="/embed" element={<Valuation />} />
          <Route path="/register" element={<RegisterApplicant />} />
          <Route path="/embed/register" element={<RegisterApplicant />} />
          <Route path="/embed/listings" element={<PublicListings />} />
          <Route path="/properties" element={<PublicListings />} />
          <Route path="/properties/:id" element={<PublicListingDetail />} />
          <Route path="/contact" element={<AgencyContact />} />
          <Route path="/sell" element={<AgencySell />} />
          <Route path="/let" element={<AgencyLet />} />
          <Route path="/guides" element={<AgencyGuides />} />
          <Route path="/guides/:guideSlug" element={<AgencyGuideDetail />} />
          <Route path="/area/:area" element={<AreaLandingPage />} />
          <Route path="/area/:area/house-prices" element={<AreaPricesPage />} />
          <Route path="/portal" element={<PortalLogin />} />
          <Route path="/portal/consume/:token" element={<PortalConsume />} />
          <Route path="/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/portal/buyer" element={<BuyerPortal />} />
          <Route path="/portal/buyer/:token" element={<BuyerPortal />} />
          <Route path="/book" element={<BookShowing />} />
          <Route path="/booking/:token" element={<ManageShowing />} />
          <Route path="/fees" element={<AgencyFees />} />
          <Route path="/terms" element={<AgencyLegal kind="terms" />} />
          <Route path="/privacy" element={<AgencyLegal kind="privacy" />} />
          <Route path="/cookies" element={<AgencyLegal kind="cookies" />} />

          {/* Foundation Studio — free UK property calculators (always Foundation branded) */}
          <Route path="/calculators" element={<CalculatorsIndex />} />
          <Route path="/calculators/mortgage" element={<MortgageCalculator />} />
          <Route path="/calculators/affordability" element={<AffordabilityCalculator />} />
          <Route path="/calculators/rental-return" element={<RentalReturnCalculator />} />
          <Route path="/calculators/closing-costs" element={<ClosingCostsCalculator />} />
          {/* The UK five are gone: Stamp Duty is a British tax, moving costs
              priced a British move, and the other three were sterling with no
              property tax, insurance escrow or PMI in them. These paths are
              kept as redirects because an agency may already have embedded
              one on their own website. */}
          <Route path="/calculators/stamp-duty" element={<Navigate to="/calculators/closing-costs" replace />} />
          <Route path="/calculators/moving-costs" element={<Navigate to="/calculators/closing-costs" replace />} />
          <Route path="/calculators/yield" element={<Navigate to="/calculators/rental-return" replace />} />
          <Route path="/calculators/btl-roi" element={<Navigate to="/calculators/rental-return" replace />} />

          {/* Admin — email-scoped tenant login. Wrapped in its own
              BrandProvider that reads tenant_slug from the login-response
              stashed in localStorage so the admin chrome (logo, palette,
              phone) matches the signed-in agency, not the default. */}
          <Route
            path="/admin/login"
            element={<AdminScoped><AdminLogin /></AdminScoped>}
          />
          {/* Self-service password reset — pre-auth, standalone (no
              AdminScoped, same as accept-invite): request a link, then set
              a new password from the emailed token. */}
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          {/* Public, password-less entry into the self-serve CRM demo —
              silently logs in against the demo_mode-flagged `foundation`
              tenant (see server.py's /demo/crm-login) and drops the
              visitor straight into /crm. No login form. */}
          <Route path="/crm-demo" element={<CrmDemoEntry />} />
          <Route path="/start" element={<StartTrial />} />
          <Route
            path="/admin"
            element={<AdminScoped><AdminDashboard /></AdminScoped>}
          />
          <Route
            path="/admin/brand"
            element={<AdminScoped><AdminBrand /></AdminScoped>}
          />
          <Route
            path="/admin/help"
            element={<AdminScoped><AdminHelp /></AdminScoped>}
          />

          {/* Foundation CRM — full standalone module. Same auth as /admin
              so the tenant-scoped BrandProvider styles it correctly. */}
          <Route path="/crm" element={<AdminScoped><CrmLayout /></AdminScoped>}>
            <Route index element={<CrmOverview />} />
            <Route path="contacts" element={<CrmContacts />} />
            <Route path="plans" element={<CrmPlans />} />
            <Route path="reviews" element={<CrmReviews />} />
            <Route path="chat" element={<CrmWebChat />} />
            <Route path="office-inbox" element={<CrmOfficeInbox />} />
            <Route path="writing" element={<CrmWriting />} />
            <Route path="contacts/:id" element={<CrmContactDetail />} />
            <Route path="properties" element={<CrmProperties />} />
            <Route path="properties/:id" element={<CrmPropertyDetail />} />
            <Route path="offers" element={<CrmOffers />} />
            <Route path="transactions" element={<CrmTransactions />} />
            <Route path="referrals" element={<CrmReferrals />} />
            <Route path="commission-export" element={<CrmCommissionExport />} />
            {/* The buy side. `tour` is before `:contactId` only in reading
                order; React Router matches the static segment either way. */}
            <Route path="buyers" element={<CrmBuyers />} />
            <Route path="online-showings" element={<CrmOnlineShowings />} />
            <Route path="buyers/:contactId" element={<CrmBuyerDetail />} />
            <Route path="buyers/:contactId/tour" element={<CrmBuyerTour />} />
            {/* Under `buyers/` so the title effect can find it: that one
                matches the LONGEST nav path a route starts with, and
                /crm/buyer-homes/... starts with no nav row at all, so every
                tab of it read "Statehouse" and nothing else. The file still
                hangs off the HOME rather than the buyer (one buyer can have
                two deals and neither is "theirs"); `homes` is a static
                segment so React Router ranks it above :contactId. */}
            <Route path="buyers/homes/:homeId/file" element={<CrmBuyerFile />} />
            <Route path="goals" element={<CrmGoals />} />
            <Route path="smart-lists" element={<CrmSmartLists />} />
            <Route path="import" element={<CrmImport />} />
            <Route path="speed-to-lead" element={<CrmSpeedToLead />} />
            <Route path="open-houses" element={<CrmOpenHouses />} />
            <Route path="seller-report" element={<CrmSellerReport />} />
            <Route path="state" element={<CrmStateRules />} />
            <Route path="ask" element={<CrmAsk />} />
            <Route path="viewings" element={<CrmViewings />} />
            <Route path="tasks" element={<CrmTasksScreen />} />
            <Route path="team-board" element={<CrmTeamBoardScreen />} />
            {/* The link in a time-off email, which names the job rather than the screen. */}
            <Route path="time-off" element={<MovedRoute to="/crm/team-board#time-off" />} />
            <Route path="lettings" element={<CrmLettingsScreen />} />
            <Route path="website" element={<CrmWebsite />} />
            <Route path="settings" element={<CrmSettings />} />
            <Route path="portals" element={<CrmExports />} />
            <Route path="signal" element={<CrmSignal />} />

            {/* US names for the same five screens. An American agent does not
                hold a "viewing", take an "appraisal", or let a "letting", and
                a URL is read: /crm/viewings in the address bar of a product
                sold as built-for-America is a small, repeated tell that it
                was not. Aliases rather than renames, because the UK names are
                the ones in existing links, bookmarks and support replies, and
                both resolve to the same component. The US nav points at
                these; see lib/crmNav.js. */}
            <Route path="showings" element={<CrmViewings />} />
            <Route path="rentals" element={<CrmLettingsScreen />} />
            <Route path="cmas" element={<CrmAppraisals />} />
            <Route path="mls" element={<CrmExports />} />

            {/* Moved, not removed. Every one of these URLs is in somebody's
                bookmarks or in an address-bar autocomplete; the screen is one
                level down now and the old link still lands on it. */}
            <Route path="team" element={<MovedRoute to="/crm/settings#team" />} />
            <Route path="templates" element={<MovedRoute to="/crm/settings#templates" />} />
            <Route path="branches" element={<MovedRoute to="/crm/settings#branches" />} />
            <Route path="terms" element={<MovedRoute to="/crm/settings#terms" />} />
            <Route path="documents" element={<MovedRoute to="/crm/settings#documents" />} />
            <Route path="brand" element={<MovedRoute to="/crm/settings#brand" />} />
            <Route path="guides" element={<MovedRoute to="/crm/website#guides" />} />
            <Route path="reach" element={<MovedRoute to="/crm/website#search" />} />
            <Route path="follow-ups" element={<MovedRoute to="/crm/tasks#follow-ups" />} />
            <Route path="sequences" element={<MovedRoute to="/crm/tasks#follow-ups" />} />
            <Route path="exports" element={<MovedRoute to="/crm/portals" />} />
            {/* The LIST of inventories folded into Lettings; a single
                inventory keeps its own URL, because that one is a document
                an agent opens from an email. */}
            <Route path="inventory" element={<MovedRoute to="/crm/lettings#inventories" />} />
            <Route path="accounts" element={<CrmAccounts />} />
            <Route path="invoices" element={<CrmInvoices />} />
            <Route path="appraisals" element={<CrmAppraisals />} />
            <Route path="calendar" element={<CrmCalendar />} />
            <Route path="inventory/:id" element={<CrmInventoryDetail />} />
            <Route path="social" element={<CrmSocial />} />
            <Route path="emails" element={<CrmEmails />} />
            <Route path="reports" element={<CrmReports />} />
            {/* Brand settings + valuation leads, nested here (not under
                /admin) so the CRM's left sidebar stays mounted and
                accessible on every page, not just the main CRM sections. */}
            <Route path="design/states" element={<DesignStates />} />
            <Route path="leads" element={<AdminDashboard embedded />} />
            <Route path="help" element={<AdminHelp embedded />} />
            <Route path="*" element={<NotFound embedded homeHref="/crm" homeLabel="Back to CRM overview" />} />
          </Route>

          {/* Catch-all — must stay last in this route group so every named
              route above gets first pick; only unmatched paths fall through
              here. Without this, a bad/old link silently rendered nothing:
              a blank page with a 200 status. */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Slug-scoped tenant surfaces — every /t/:slug/* branch loads
            THAT tenant's brand into its own BrandProvider so multiple
            tenants can be previewed side-by-side. */}
        <Route
          path="/t/:slug/*"
          element={
            <TenantScope>
              <TenantRoutes />
            </TenantScope>
          }
        />
      </Routes>
      </Suspense>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

/**
 * Nothing renders until the brand call has settled.
 *
 * This used to be BrandedOrFoundation: a choice between the agency's own site
 * and Foundation's marketing homepage, decided by whether the hostname
 * resolved to a real tenant. Statehouse has no marketing page inside the app
 * to fall back to — that site is its own repo — so there is no choice left to
 * make, only the wait.
 *
 * The wait is the part worth keeping. Falling through while the slug was
 * still null was true on EVERY first paint, so loading an agency's own domain
 * painted the software vendor's website first and swapped a moment later. On
 * a fast connection that is a flicker; on mobile it is a second or more of
 * somebody else's company sitting on the customer's domain, which is the
 * opposite of white-label. A blank moment is the right trade.
 */
function BrandedPage({ children }) {
  const { brand_resolved } = useBrand();
  if (!brand_resolved) return <div style={{ minHeight: "100vh" }} />;
  return children;
}

/**
 * ValuationRoute — US tenants get the CMA-request flow in place of the UK
 * instant-AVM tool, which is bound to HM Land Registry / EPC data with no
 * US equivalent. UK tenants keep the instant valuation.
 */
function ValuationRoute() {
  const market = useMarket();
  return market === "us" ? <CmaRequest /> : <Valuation />;
}

/** Wraps the app-level default-brand routes in a single BrandProvider. */
function AppShell() {
  return (
    <BrandProvider>
      <div className="App">
        <Outlet />
      </div>
    </BrandProvider>
  );
}

/** Tenant-scoped routes rendered inside a per-tenant BrandProvider. */
function TenantRoutes() {
  return (
    <div className="App">
      <Routes>
        <Route index element={<AgencySite />} />
        <Route path="try" element={<Landing />} />
        <Route path="home" element={<AgencySite />} />
        <Route path="valuation" element={<ValuationRoute />} />
        <Route path="market-report" element={<MarketReport />} />
        <Route path="embed" element={<Valuation />} />
        <Route path="register" element={<RegisterApplicant />} />
        <Route path="embed/register" element={<RegisterApplicant />} />
        <Route path="embed/listings" element={<PublicListings />} />
        <Route path="properties" element={<PublicListings />} />
        <Route path="properties/:id" element={<PublicListingDetail />} />

        {/* Foundation Reach — one SEO landing page per area served,
            generated from Brand → Areas served. */}
        <Route path="area/:area" element={<AreaLandingPage />} />
        <Route path="area/:area/house-prices" element={<AreaPricesPage />} />

        {/* Client portals — vendor / landlord / buyer / tenant self-service.
            Inherit tenant brand from the outer TenantScope. */}
        <Route path="portal" element={<PortalLogin />} />
        <Route path="portal/consume/:token" element={<PortalConsume />} />
        <Route path="portal/dashboard" element={<PortalDashboard />} />
        {/* The buyer's own page. Under /portal so it shares the installed
            client app's scope with the seller's sign-in. */}
        <Route path="portal/buyer" element={<BuyerPortal />} />
        <Route path="portal/buyer/:token" element={<BuyerPortal />} />

        {/* Online showing requests. `book` is the public page;
            `booking/:token` is the link in the confirmation. */}
        <Route path="book" element={<BookShowing />} />
        <Route path="booking/:token" element={<ManageShowing />} />

        {/* Foundation Sites Phase 2 — About + Contact sub-pages. */}
        <Route path="about" element={<AgencyAbout />} />
        <Route path="contact" element={<AgencyContact />} />
        <Route path="sell" element={<AgencySell />} />
        <Route path="let" element={<AgencyLet />} />
        <Route path="guides" element={<AgencyGuides />} />
        <Route path="guides/:guideSlug" element={<AgencyGuideDetail />} />
        <Route path="fees" element={<AgencyFees />} />
        <Route path="terms" element={<AgencyLegal kind="terms" />} />
        <Route path="privacy" element={<AgencyLegal kind="privacy" />} />
        <Route path="cookies" element={<AgencyLegal kind="cookies" />} />
        <Route path="*" element={<TenantNotFound />} />
      </Routes>
    </div>
  );
}

/** 404 for a tenant's own site — same chrome (header/footer) as every
    other tenant page, "back home" resolves to that tenant's own root. */
function TenantNotFound() {
  const { tenant_slug } = useBrand();
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <div className="flex-1">
        <NotFound embedded homeHref={tenant_slug ? `/t/${tenant_slug}` : "/"} homeLabel="Back home" />
      </div>
      <SiteFooter />
    </div>
  );
}

/**
 * AdminScoped — wraps /admin/* in a BrandProvider.
 *
 * - Before login (no session in localStorage): show Foundation branding
 *   so the sign-in page reads as "Foundation" rather than defaulting to
 *   the showcase tenant.
 * - After login: read the slug from the login response stashed in
 *   localStorage so the tenant's brand (logo, colours, phone) drives
 *   the admin/CRM chrome.
 */
function AdminScoped({ children }) {
  const raw = typeof window !== "undefined"
    ? window.localStorage.getItem("wb_admin_tenant")
    : null;
  let slug = null;
  try { slug = raw ? JSON.parse(raw).slug : null; } catch { slug = null; }
  // No session → force the Foundation product brand on the login screen.
  return <BrandProvider slug={slug || "foundation"}>{children}</BrandProvider>;
}
